import { VehiclePolylineItemResponse } from '../types/get-vehicle-polylines';

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object';

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const isTransitSection = (value: unknown): boolean =>
  isRecord(value) && isFiniteNumber(value.startVertexIndex) && isFiniteNumber(value.endVertexIndex);

const isVehiclePolyline = (value: unknown): value is VehiclePolylineItemResponse =>
  isRecord(value) &&
  isFiniteNumber(value.vehicleId) &&
  typeof value.polyline === 'string' &&
  isRecord(value.boundingBox) &&
  isFiniteNumber(value.boundingBox.minimumLongitude) &&
  isFiniteNumber(value.boundingBox.minimumLatitude) &&
  isFiniteNumber(value.boundingBox.maximumLongitude) &&
  isFiniteNumber(value.boundingBox.maximumLatitude) &&
  (value.transitSections === undefined ||
    (Array.isArray(value.transitSections) && value.transitSections.every((section) => isTransitSection(section))));

const getContent = (response: unknown): Record<string, unknown> => {
  if (!isRecord(response) || !isRecord(response.content)) {
    throw new TypeError('Vehicle polyline response is missing content.');
  }

  return response.content;
};

export const parseVehiclePolylines = (response: unknown): VehiclePolylineItemResponse[] => {
  const items = getContent(response).items;

  if (!Array.isArray(items) || !items.every(isVehiclePolyline)) {
    throw new TypeError('Vehicle polyline response contains invalid items.');
  }

  return items;
};

export const parseVehiclePolylineArtifactUrl = (response: unknown, artifactOrigin: string): string => {
  const url = getContent(response).url;

  if (typeof url !== 'string' || url.length === 0) {
    throw new TypeError('Vehicle polyline artifact response is missing a URL.');
  }

  let parsedUrl: URL;

  try {
    parsedUrl = new URL(url);
  } catch {
    throw new TypeError('Vehicle polyline artifact response contains an invalid URL.');
  }

  if (parsedUrl.protocol !== 'https:') {
    throw new TypeError('Vehicle polyline artifact response URL must use HTTPS.');
  }

  if (![artifactOrigin].includes(parsedUrl.origin)) {
    throw new TypeError('Vehicle polyline artifact response URL has an unexpected origin.');
  }

  return url;
};
